import { useState } from 'react';
import { useSelector } from 'react-redux';
import { getUserId } from 'store/session/selectors';
import { useNavigate, useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { Card } from 'components/@shared/Card';
import { ProposalFormData } from 'types/proposal';
import { BackdropLoader } from 'components/@shared/Loaders';
import { useNotifications } from 'hooks/useNotifications';
import { PATH } from 'routes/path';
import { createNewProposal } from 'services/proposalService';
import { useDidMount } from 'hooks/useDidMount';
import { ProposalForm } from 'components/Proposals/ProposalForm';
import { getAppointmentDetails } from 'services/appointmentService';
import { initialProposalFormDataValues } from 'components/Proposals/ProposalForm/constants';
import { ONLY_CONTRACTOR } from 'constants/roles';
import { useDeniedAccess } from 'hooks/useDeniedAccess';
import { parametrizeURL } from 'routes/utils';

const cardStyles = {
    maxWidth: '700px',
    margin: 'auto',
};
export const CreateProposalPage = () => {
    const userId = useSelector(getUserId);
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState<ProposalFormData | null>(null);

    const notifications = useNotifications();
    const navigate = useNavigate();
    const { appointmentId } = useParams<{ appointmentId: string }>();

    const submitHandler = (form: ProposalFormData) => {
        setIsLoading(true);
        createNewProposal(form)
            .then(() => {
                notifications.show({ message: 'Appointment successfully created' });
                if (appointmentId) navigate(parametrizeURL(PATH.APPOINTMENT_DETAILS, { id: appointmentId }));
            })
            .finally(() => setIsLoading(false));
    };

    useDidMount(() => {
        setIsLoading(true);
        if (appointmentId && userId) {
            getAppointmentDetails(appointmentId)
                .then(({ id }) => {
                    setFormData({
                        ...initialProposalFormDataValues,
                        contractorId: userId,
                        appointmentId: id,
                    });
                })
                .finally(() => setIsLoading(false));
        }
    });

    useDeniedAccess(ONLY_CONTRACTOR);

    return (
        <Box width="100%" px={2.5} py={2}>
            {formData && (
                <Card title="Proposal" sx={cardStyles}>
                    <ProposalForm initialData={formData} onSubmit={submitHandler} />
                </Card>
            )}
            {isLoading && <BackdropLoader />}
        </Box>
    );
};
